import { MultiStrikeChartTypes } from '../../../../reducer/multiStrikeChart.reducer'
import { isOiValueChartType } from '../../multistrikeChartTypeHelpers'
import { formatOiQuantity } from '../../../../util/screener'
import { LiveTextSelector } from '../../../common/LiveText'
import {
    getEffectiveMultiStrikeSeriesPoint,
    isMultiStrikeCrosshairSnapshotActive,
    subscribeMultiStrikeLiveData,
} from '../../multiStrikeChartSnapshotStore'
import Chart from '../../chart'
import { resolveSeriesOutlookDisplay } from '../../chartMetadataHelpers'

const selectorCache = new Map<string, LiveTextSelector>()
const outlookSelectorCache = new Map<string, { selectText: LiveTextSelector; selectColor: LiveTextSelector }>()

const formatMultiStrikeValue = (value: number | undefined, chartType: MultiStrikeChartTypes): string => {
    if (value === undefined || !Number.isFinite(value)) return '-'
    if (isOiValueChartType(chartType)) {
        return formatOiQuantity(value)
    }
    return value.toFixed(2)
}

/** Underlying security price — plain decimal, never abbreviated (e.g. no K/M). */
export const formatMultiStrikePrimaryValue = (value: number | undefined): string => {
    if (value === undefined || !Number.isFinite(value)) return '-'
    return value.toFixed(2)
}

export const buildMultiStrikeMetadataSubscribe = (viewKey: string, seriesKeys: string[]) => {
    return (listener: () => void) => subscribeMultiStrikeLiveData(viewKey, seriesKeys, listener)
}

export const getMultiStrikeValueSelector = (
    viewKey: string,
    seriesKey: string,
    chartType: MultiStrikeChartTypes
): LiveTextSelector => {
    const cacheKey = `${viewKey}:${seriesKey}:${chartType}`
    if (!selectorCache.has(cacheKey)) {
        selectorCache.set(cacheKey, () => {
            const point = getEffectiveMultiStrikeSeriesPoint(viewKey, seriesKey)
            return formatMultiStrikeValue(point?.value, chartType)
        })
    }
    return selectorCache.get(cacheKey)!
}

export const getMultiStrikeOutlookSelectors = (
    viewKey: string,
    seriesKey: string | undefined,
    getChart: () => Chart | undefined
): { selectText: LiveTextSelector; selectColor: LiveTextSelector } => {
    const cacheKey = `${viewKey}:${seriesKey ?? ''}:outlook`
    if (!outlookSelectorCache.has(cacheKey)) {
        outlookSelectorCache.set(cacheKey, {
            selectText: () => {
                if (!seriesKey) return '-'
                const chart = getChart()
                const crosshairInfo = isMultiStrikeCrosshairSnapshotActive(viewKey)
                    ? chart?.crossHairInfo
                    : undefined
                const resolved = resolveSeriesOutlookDisplay(crosshairInfo, chart, seriesKey)
                return resolved.tag
            },
            selectColor: () => {
                if (!seriesKey) return ''
                const chart = getChart()
                const crosshairInfo = isMultiStrikeCrosshairSnapshotActive(viewKey)
                    ? chart?.crossHairInfo
                    : undefined
                const resolved = resolveSeriesOutlookDisplay(crosshairInfo, chart, seriesKey)
                return resolved.color
            },
        })
    }
    return outlookSelectorCache.get(cacheKey)!
}

export const getMultiStrikePrimaryValueSelector = (
    viewKey: string,
    seriesKey: string
): LiveTextSelector => {
    const cacheKey = `${viewKey}:${seriesKey}:primary`
    if (!selectorCache.has(cacheKey)) {
        selectorCache.set(cacheKey, () => {
            const point = getEffectiveMultiStrikeSeriesPoint(viewKey, seriesKey)
            return formatMultiStrikePrimaryValue(point?.value)
        })
    }
    return selectorCache.get(cacheKey)!
}
